/**
 * Add Custom Section Modal
 * Create new custom sections (AI or manual)
 */

import React, { useState } from 'react';
import { Modal, Button, Input, Select } from '../UI';
import { FileText, Brain, Type, Loader2 } from 'lucide-react';
import { type ReportSection } from '../../api/reports';

interface AddCustomSectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportId: string;
  onSectionCreated: (section: ReportSection) => void;
  defaultPosition?: number;
}

export function AddCustomSectionModal({
  isOpen,
  onClose,
  reportId,
  onSectionCreated,
  defaultPosition,
}: AddCustomSectionModalProps) {
  const [sectionName, setSectionName] = useState('');
  const [sectionType, setSectionType] = useState<'standard' | 'custom' | 'appendix' | 'table' | 'figure' | 'staff' | 'past_project'>('custom');
  const [contentMode, setContentMode] = useState<'ai' | 'manual'>('ai');
  const [question, setQuestion] = useState('');
  const [manualContent, setManualContent] = useState('');
  const [position, setPosition] = useState<number | undefined>(defaultPosition);
  const [loading, setLoading] = useState<boolean>(false);

  // Placeholder / filler prompt templates per section type (for AI-generated sections)
  const SECTION_TYPE_PROMPTS: Record<string, string> = {
    standard: 'Describe the key points for this section. (e.g., Summarize our approach and deliverables)',
    custom: 'What should this section cover? (e.g., Describe our approach to quality assurance)',
    staff: 'Write a key person / staff resource write-up. Include: role, qualifications, relevant experience, and how they meet the RFP evaluation criteria for this position.',
    past_project: 'Describe a relevant past project. Include: project name, client, period, scope, your role, outcomes, and how it demonstrates capability for this RFP.',
    appendix: 'Provide supporting material for this appendix. (e.g., Data, tables, or references that support the proposal)',
    table: 'What data or comparison should this table show? (e.g., Team qualifications matrix, schedule milestones, or cost breakdown)',
    figure: 'Describe the figure or diagram content. (e.g., Process flow, organization chart, or technical architecture)',
  };
  const questionPlaceholder = SECTION_TYPE_PROMPTS[sectionType] ?? SECTION_TYPE_PROMPTS.custom;

  const handleSubmit = () => {
    setLoading(true);
    if (!sectionName.trim()) {
      alert('Section name is required');
      setLoading(false);
      return;
    }

    if (contentMode === 'ai' && !question.trim()) {
      alert('Question is required for AI-generated sections');
      setLoading(false);
      return;
    }

    if (contentMode === 'manual' && !manualContent.trim()) {
      alert('Content is required for manual sections');
      setLoading(false);
      return;
    }

    // Create section object (will be sent to API)
    const section: Partial<ReportSection> = {
      id: '', // Will be set by API
      section_name: sectionName,
      section_type: sectionType,
      content_mode: contentMode,
      question: contentMode === 'ai' ? question : sectionName,
      answer: contentMode === 'manual' ? manualContent : undefined,
      display_order: position ?? 0,
    };

    onSectionCreated(section as ReportSection);

    // Reset form
    setSectionName('');
    setSectionType('custom');
    setContentMode('ai');
    setQuestion('');
    setManualContent('');
    setPosition(undefined);
    setLoading(false);
    // Close immediately; CustomSectionsTab will optimistically render the new section.
    onClose();
  };

  const handleSectionTypeChange = (newType: typeof sectionType) => {
    setSectionType(newType);
    // Pre-fill question with type-specific template when empty or matches a known template
    const prompts = SECTION_TYPE_PROMPTS;
    if (!question || Object.values(prompts).includes(question)) {
      setQuestion(prompts[newType] ?? prompts.custom);
    }
  };

  const handleCancel = () => {
    setSectionName('');
    setSectionType('custom');
    setContentMode('ai');
    setQuestion('');
    setManualContent('');
    setPosition(undefined);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Add Custom Section"
      size="lg"
    >
      <div className="space-y-4">
        {/* Section Name */}
        <Input
          label="Section Name"
          value={sectionName}
          onChange={(e) => setSectionName(e.target.value)}
          placeholder="e.g., Executive Summary, Appendix A"
          required
        />

        {/* Section Type */}
        <Select
          label="Section Type"
          value={sectionType}
          onChange={(e) => handleSectionTypeChange(e.target.value as typeof sectionType)}
          options={[
            { value: 'standard', label: 'Standard' },
            { value: 'custom', label: 'Custom' },
            { value: 'staff', label: 'Staff (key person / resource write-up)' },
            { value: 'past_project', label: 'Past Project (relevant project experience)' },
            { value: 'appendix', label: 'Appendix' },
            { value: 'table', label: 'Table' },
            { value: 'figure', label: 'Figure' },
          ]}
        />

        {/* Content Mode */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Content Mode
          </label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                value="ai"
                checked={contentMode === 'ai'}
                onChange={(e) => setContentMode(e.target.value as 'ai' | 'manual')}
                className="w-4 h-4"
              />
              <Brain className="h-4 w-4" />
              <span>AI Generated (RAG)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                value="manual"
                checked={contentMode === 'manual'}
                onChange={(e) => setContentMode(e.target.value as 'ai' | 'manual')}
                className="w-4 h-4"
              />
              <Type className="h-4 w-4" />
              <span>Manual Entry</span>
            </label>
          </div>
        </div>

        {/* AI Mode: Question */}
        {contentMode === 'ai' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Question for AI Generation
            </label>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={questionPlaceholder}
              rows={4}
              required
              className="block w-full rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
            />
          </div>
        )}

        {/* Manual Mode: Content */}
        {contentMode === 'manual' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Section Content
            </label>
            <textarea
              value={manualContent}
              onChange={(e) => setManualContent(e.target.value)}
              placeholder="Enter the section content..."
              rows={8}
              required
              className="block w-full rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
            />
          </div>
        )}

        {/* Position (Optional) */}
        <Input
          label="Position (optional)"
          type="number"
          value={position?.toString() || ''}
          onChange={(e) => setPosition(e.target.value ? parseInt(e.target.value) : undefined)}
          placeholder="Leave empty to append at end"
          min={0}
        />

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
            Create Section
          </Button>
        </div>
      </div>
    </Modal>
  );
}
